
import { useState } from 'react';
import { Farm } from '@/types/database.types';
import { parseFarmExcelFile, uploadFarmsToDatabase, generateFarmExcelTemplate } from '@/utils/excel';
import { useToast } from '@/hooks/use-toast';
import { createHarvest } from '@/services/harvestService';

export const useFarmUpload = (onUploadComplete: () => void) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadState, setUploadState] = useState<'idle' | 'parsing' | 'uploading' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<string[]>([]);
  const [parsedData, setParsedData] = useState<Partial<Farm>[]>([]);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadState('idle');
      setErrors([]);
      setParsedData([]);
    }
  };

  // Enhanced function to create initial harvests for each farm
  const createInitialHarvests = async (farms: Farm[]) => {
    try {
      console.log('Creating initial harvests for farms:', farms);
      
      // Track successfully created harvests for reporting
      let createdHarvestsCount = 0;
      
      for (const farm of farms) {
        console.log('Processing farm for harvest creation:', farm.id, farm.farmer_name);
        console.log('Farm estimated harvest quantity:', farm.estimated_harvest_kg);
        
        // Always create an initial planned harvest for each farm
        // Use either the specific starting_date, harvest_start_date or current date if not specified
        const harvestDate = farm.harvest_start_date || new Date().toISOString().split('T')[0];
        
        // Create the harvest record with proper fields, ensuring the expected_quantity is set from estimated_harvest_kg
        await createHarvest({
          farm_id: farm.id,
          status: 'planned',
          expected_date: harvestDate,
          expected_quantity: farm.estimated_harvest_kg,
          actual_date: null,
          actual_quantity: null,
          quality_grade: null,
          notes: `Initial harvest record created from farm upload for ${farm.farmer_name}`,
          product_name: farm.product_name || null // Include the product_name from farm
        });
        
        console.log(`Created harvest for farm ${farm.id} with date ${harvestDate} and quantity ${farm.estimated_harvest_kg !== null ? farm.estimated_harvest_kg : 'not specified'}`);
        createdHarvestsCount++;
      }
      
      console.log(`Successfully created ${createdHarvestsCount} harvest records`);
      return createdHarvestsCount;
    } catch (error) {
      console.error('Error creating initial harvests:', error);
      throw new Error(`Failed to create harvest records: ${(error as Error).message}`);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setIsUploading(true);
      setUploadState('parsing');
      setProgress(25);

      const { data, errors } = await parseFarmExcelFile(file);
      
      setParsedData(data);
      setErrors(errors);
      
      if (errors.length > 0) {
        console.warn('Parsing warnings/errors:', errors);
      }
      
      if (data.length === 0) {
        throw new Error('No valid entries found in the Excel file. Please check that all required fields are filled in.');
      }
      
      setProgress(50);
      setUploadState('uploading');
      
      try {
        const result = await uploadFarmsToDatabase(data);
        console.log('Farms uploaded to database:', result);
        
        setProgress(75);
        
        // Create initial harvest records for the uploaded farms
        let harvestCount = 0;
        if (result.data && result.data.length > 0) {
          console.log('Creating harvests for uploaded farms...');
          harvestCount = await createInitialHarvests(result.data);
        }
        
        setProgress(100);
        setUploadState('success');
        
        toast({
          title: 'Upload successful',
          description: `${result.count} farms have been uploaded with ${harvestCount} harvest records.`,
        });
        
        onUploadComplete();
        
        setTimeout(() => {
          setFile(null);
          setProgress(0);
          setUploadState('idle');
          const fileInput = document.getElementById('farm-excel-upload') as HTMLInputElement;
          if (fileInput) fileInput.value = '';
        }, 3000);
      } catch (error: any) {
        console.error('Upload error:', error);
        setUploadState('error');
        
        // Check for the specific date format error and provide a custom message
        if (error.message && error.message.includes('invalid input syntax for type date')) {
          toast({
            title: 'Date Format Error',
            description: 'The date format in your Excel file is incorrect. Please use YYYY-MM-DD format for all dates, not Excel\'s internal date format.',
            variant: 'destructive'
          });
          
          // Add more specific error to the errors list to display in the UI
          setErrors([
            ...errors, 
            'Date format error: Please ensure all dates are in YYYY-MM-DD format. Excel sometimes stores dates as numbers (like "45711"). Please format your date columns as Text in Excel before entering the dates.'
          ]);
        } else {
          toast({
            title: 'Upload failed',
            description: (error as Error).message,
            variant: 'destructive'
          });
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadState('error');
      
      toast({
        title: 'Upload failed',
        description: (error as Error).message,
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Convert the template to a Blob
    const templateData = generateFarmExcelTemplate();
    const blob = new Blob([templateData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'farm_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    file,
    isUploading,
    progress,
    uploadState,
    errors,
    parsedData,
    handleFileChange,
    handleUpload,
    downloadTemplate: handleDownloadTemplate
  };
};
